/* eslint-disable import/prefer-default-export */
import type { CommonDetail } from '@src/endpoints/services/graphQueryMgr.service'
import { map, isString, isNumber, compact } from 'lodash'
import { getAccountNameById } from '@src/utils/accounts'
import {
  getCountPolicies,
  parsePolicies,
  Policies
} from '@src/utils/policy.utils'
import {
  createScopeInstance,
  getScopeDisplayName
} from '@src/utils/scope.utils'
import type { AuthState } from '@src/redux/reducers/auth'
import { getAccountIdFromUri } from '@src/utils/url.utils'

interface Data<T> {
  items?: T[]
}

interface Common {
  common_info?: CommonDetail
  policies?: string
  url?: string
  name?: string
}

const parseJSONPolicies = (data: string) => {
  try {
    return JSON.parse(data || 'null') || []
  } catch (ex) {
    return []
  }
}

export function getPolicies(policies: string | number) {
  return isString(policies)
    ? parsePolicies(parseJSONPolicies(policies))
    : policies
}

export function getPoliciesCount(policies: number | Policies[]) {
  return isNumber(policies) ? policies : getCountPolicies(policies)
}

export function getUserUriFromUrl(pathname: string) {
  const parts = compact((pathname || '').split('/'))
  if (parts.length === 6) {
    return getAccountIdFromUri(parts[5])
  }
  return ''
}

export const parseItem = <T extends Common>(item: T, auth: AuthState): T => {
  const { tenant, singleAccounts, organizations } = auth || {}
  const { account_info = [] } = tenant || {}
  const account_id = item?.common_info?.account_id || ''
  const account_name = getAccountNameById(account_id, account_info)
  const policies = getPolicies(item?.policies)
  const mergedItem = { ...item, ...item.common_info }
  const scope = createScopeInstance(mergedItem?.scope).parse({
    singleAccounts,
    organizations
  }).value

  return {
    ...mergedItem,
    scope_display_name: getScopeDisplayName(scope),
    id: mergedItem?.url || mergedItem?.name,
    account_name,
    policy_account_id: getUserUriFromUrl(window.location.pathname),
    policies_cnt: getPoliciesCount(policies),
    policies
  }
}

export const parseItems = <T extends Common>(
  list: T[] = [],
  auth: AuthState
): T[] => map(list, x => parseItem(x, auth))

export const parseDetails = <D extends Common, T extends Data<D>>(
  data: T,
  auth: AuthState
) => ({
  ...data,
  items: parseItems(data?.items || [], auth)
})
